import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthUserActions } from "../context/authUser.context";
import { pages } from "../utils/common";
import { localStorageGet } from "../utils/localStorage";

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export const useSessionTimeout = () => {
  const navigate = useNavigate();
  const { logout } = useAuthUserActions();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const handleTimeout = useCallback(() => {
    const token = localStorageGet("token");
    if (token) {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity >= SESSION_TIMEOUT_MS) {
        logout();
        navigate(pages.auth.path);
      }
    }
  }, [logout, navigate]);

  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(handleTimeout, SESSION_TIMEOUT_MS);
  }, [handleTimeout]);

  useEffect(() => {
    const token = localStorageGet("token");
    if (!token) {
      return;
    }

    // Set up activity listeners
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleActivity = () => {
      resetTimeout();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    resetTimeout();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimeout]);
};
